import { gsap } from 'gsap/all';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin);
import regex_email from '../utils/regex/email';

const defaultOptions = {
  selectorGroup: '.form-group',
  classErrorElement: 'form-error',
  classIsError: 'is-error',
  animateToFirstError: false,
  i18n: {
    required: Translator ? Translator.trans('form_errors.pattern') : 'Ce champs est obligatoire',
    same: Translator ? Translator.trans('form_errors.same') : 'Les champs ne correspondent pas.',
    email: Translator ? Translator.trans('form_errors.email') : 'Merci de renseigner un email valide.',
    empty: Translator ? Translator.trans('form_errors.empty') : 'Ce champs est obligatoire.',
    checkbox: Translator ? Translator.trans('form_errors.checkbox') : 'Veuillez accepter les conditions.',
    radio: Translator ? Translator.trans('form_errors.radio') : 'Veuillez sélectionner au moins un choix.',
    pattern: Translator ? Translator.trans('form_errors.pattern') : 'La donnée saisie ne respecte pas les règles énoncées.',
  }
};

export default class FormControl {
  //________________________________________________________ constructor
  // -
  /**
   * Add controls on your formulaire
   * @param {Element} form formulaire need add control
   * @param {Object} options define options of controls
   * @param {string} options.selectorGroup - The name of the employee.
   * @param {string} options.classErrorElement - The employee's department.
   * @param {string} options.classIsError - The employee's department.
   * @param {string} options.animateToFirstError - The employee's department.
   * @param {Object} options.i18n - The employee's department.
   * @param {Object} options.i18n.required - The employee's department.
   * @param {Object} options.i18n.same - The employee's department.
   * @param {Object} options.i18n.email - The employee's department.
   * @param {Object} options.i18n.empty - The employee's department.
   * @param {Object} options.i18n.checkbox - The employee's department.
   * @param {Object} options.i18n.radio - The employee's department.
   * @param {Object} options.i18n.pattern - The employee's department.
   **/
  constructor(form, options = {}) {
    /** @private */
    this._form = form;
    /** @private */
    this._dom = {};
    /** @private */
    this._options = {
      ...defaultOptions,
      ...options
    };

    this._form.setAttribute('novalidate', 'novalidate');
    this._formCreateBound();
    this._formGrabDom();
    this._formAddEvents();
  }

  set options(options) {
    this._options = {
      ...this._options,
      options
    };
  }

  get form() {
    return this._form;
  }

  get options() {
    return this._options;
  }

  /**
   * @deprecated use isValid
   */
  get isValidate() {
    return this._isValid();
  }

  get isValid() {
    return this._isValid();
  }

  //________________________________________________________ public
  // -
  destroy() {
    this._form.removeEventListener('submit', this._onFormSubmit);
    this._domForm.inputs.forEach(input => {
      input.removeEventListener('keyup', this._onChange);
    });
    this._domForm.choices.forEach(input => {
      input.removeEventListener('change', this._onChange);
    });
  }

  update() {
    this._formGrabDom();
  }

  removeErrors(el = null) {
    this._removeError(el);
  }

  setError(element, message = null) {
    const parent = element.closest(this._options.selectorGroup);
    if (parent === null) return;
    let error = parent.querySelector(`.${this._options.classErrorElement}`);
    parent.classList.add(this._options.classIsError);
    const id = `error-${[...Array(6)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')}`;
    if (!error) {
      error = Object.assign(
        document.createElement('p'), {
          id,
          className: this._options.classErrorElement,
          innerHTML: message
      });
      element.setAttribute('aria-describedby', id);
      parent.appendChild(error);
    } else {
      if (error.tagName === 'P') {
        if (message === error.innerHTML) return;
        const newError = Object.assign(
          document.createElement('ul'), {
            id,
            className: this._options.classErrorElement,
            innerHTML: `<li>${error.innerHTML}</li>`
        });
        element.setAttribute('aria-describedby', id);
        parent.appendChild(newError);
        error.parentNode.removeChild(error);
        error = newError;
      }
      const previousErrors = [...error.querySelectorAll('li')];
      for (let i = 0; i < previousErrors.length; i++) {
        if (previousErrors[i].innerHTML === message) return;
      }
      if (message) {
        const errorElement = document.createElement('li');
        errorElement.innerHTML = message;
        error.appendChild(errorElement);
      }
    }
  }

  //________________________________________________________ events
  // -
  /** @protected */
  onFormSubmit(pEvt) {
    if (!this._isValid()) {
      pEvt.preventDefault();
    }
  }

  /**
   * @deprecated use onFormSubmit
   */
  _onFormSubmit(pEvt) {
    this.onFormSubmit(pEvt);
  }

  /** @private */
  _onChange(pEvt) {
    const el = pEvt.target;
    this._removeError(el);
  }

  //________________________________________________________ private
  // -
  /** @private */
  _formCreateBound() {
    ['_onChange', '_onFormSubmit', 'onFormSubmit']
      .forEach((fn) => this[fn] = this[fn].bind(this));
  }

  /** @private */
  _removeError(el = null) {
    if (el) {
      const group = el.closest(this._options.selectorGroup);
      if (group === null) return;
      group.classList.remove(this._options.classIsError);
      const currentErrors = [...group.querySelectorAll(`.${this._options.classErrorElement}`)];
      currentErrors.forEach(error => {
        error.classList.remove(this._options.classIsError);
        error.parentNode.removeChild(error);
      });
      el.removeAttribute('aria-describedby');
    } else {
      const isErrors = [...this._form.querySelectorAll(`${this._options.classIsError}`)];
      const currentErrors = [...this._form.querySelectorAll(`.${this._options.classErrorElement}`)];
      currentErrors.forEach(error => {
        error.parentNode.removeChild(error);
      });
      isErrors.forEach(error => {
        error.classList.remove(this._options.classIsError);
      });
      this._domForm.els.forEach( input => {
        input.removeAttribute('aria-describedby');
      });
    }
  }

  /** @private */
  _checkError(el) {
    const group = el.closest(this._options.selectorGroup);
    if (group === null) return false;
    if (el.getAttribute('disabled') !== null) return false;

    let invalid = false;
    if (!testRequired(el, this._form)) {
      invalid = true;
      const type = el.getAttribute('type');
      if (type === 'radio') this.setError(el, this._options.i18n.radio);
      else if (type === 'checkbox') this.setError(el, this._options.i18n.checkbox);
      else if (type !== 'hidden') this.setError(el, this._options.i18n.empty);
    }
    if (!testEmail(el, this._form)) {
      invalid = true;
      this.setError(el, this._options.i18n.email);
    }
    if (!testPattern(el, this._form)) {
      invalid = true;
      this.setError(el, this._options.i18n.pattern);
    }
    if (!testSame(el, this._form)) {
      invalid = true;
      this.setError(el, this._options.i18n.same);
    }

    return invalid;
  }

  /** @private */
  _isValid() {
    let isValid = true,
      firstErrorElement = null;
    this._removeError();

    for (let i = 0; i < this._domForm.els.length; i++) {
      const el = this._domForm.els[i];
      const error = this._checkError(el);
      if (!error) continue;
      else isValid = false;
      if (!isValid && firstErrorElement === null) firstErrorElement = el;
    }

    if (!isValid && firstErrorElement) {
      firstErrorElement.focus();
      if (this._options.animateToFirstError) {
        gsap.to(window, {
          duration: .6,
          scrollTo: (window.pageYOffset + firstErrorElement.getBoundingClientRect().top - 150)
        })
      }
    }
    return isValid;
  }

  /** @private */
  _formGrabDom() {
    /** @private */
    this._domForm = {};
    this._domForm.inputs = [...this._form.querySelectorAll('input[type=text],input[type=number],input[type=password],input[type=hidden],input[type=search],input[type=email],input[type=tel],textarea')];
    this._domForm.choices = [...this._form.querySelectorAll('select, input[type=radio],input[type=checkbox],input[type=file]')];
    this._domForm.els = [...this._domForm.inputs, ...this._domForm.choices];
  }

  /** @private */
  _formAddEvents() {
    this._form.addEventListener('submit', this._onFormSubmit);
    this._domForm.inputs.forEach(input => {
      input.addEventListener('keyup', this._onChange);
    });
    this._domForm.choices.forEach(input => {
      input.addEventListener('change', this._onChange);
    });
  }
}

/**
 * Test required input/radio/checkbox is valid
 * @param  {Element} el     Input to test
 * @param  {Element} form   Formulaire
 * @return {Boolean} Returns if element is valid or not
 */
export function testRequired(el, form) {
  const isRequired = el.getAttribute('required');
  if (isRequired === null) return true;

  const type = el.getAttribute('type');
  if (type === 'radio') {
    const radios = [...form.querySelectorAll(`input[type="radio"][name="${el.getAttribute('name')}"]`)];
    for (let i = 0; i < radios.length; i++) {
      if (radios[i].checked) return true;
    }
    return false;
  } else if (type === 'checkbox') return el.checked;
  return el.value !== '';
}

/**
 * Test input of email type is valid
 * @param  {Element} el     Input to test
 * @param  {Element} form   Formulaire
 * @return {Boolean} Returns if element is valid or not
 */
export function testEmail(el, form) {
  const type = el.getAttribute('type');
  if (type !== 'email' || el.value === '') return true;
  return regex_email(el.value);
}

/**
 * Test element with attribute data-formvalidate-same and his twin are same
 * @param  {Element} el     Input to test
 * @param  {Element} form   Formulaire
 * @return {Boolean} Returns if element is valid or not
 */
export function testSame(el, form) {
  const hasSame = el.getAttribute('data-formvalidate-same');
  if (hasSame === null) return true;
  const twinEl = form.querySelector(`[data-formvalidate-same=${el.getAttribute('data-formvalidate-same')}]`);
  return twinEl.value === el.value;
}

/**
 * Test element with pattern is valid
 * @param  {Element} el     Input to test
 * @param  {Element} form   Formulaire
 * @return {Boolean} Returns if element is valid or not
 */
export function testPattern(el, form) {
  const pattern = el.getAttribute('pattern');
  if (pattern === null || el.value === '') return true;
  const rgx = new RegExp(pattern);
  return rgx.test(el.value);
}